import {
  ActionIcon,
  Button,
  Center,
  ComboboxItem,
  Divider,
  Group,
  Loader,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconDownload } from "@tabler/icons-react";
import { safeApiCall } from "api/common.service";
import {
  getSalesProfitMarginByMonth,
  getSalesProfitMarginYr2Date,
} from "api/report.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
} from "common/constants";
import { navigateTo } from "common/navigation";
import { formatNumberAsString, formatNZD } from "common/utils";
import ProductCodeInput from "pages/admin/components/ProductCodeInput";
import SalesMarginReportTypeSelection from "pages/admin/components/SalesMarginReportTypeSelection";
import YearSelect from "pages/admin/components/YearSelection";
import React, { useEffect, useRef, useState } from "react";
import { RptSalesProfitMargins } from "types/model.types";

const AdminSalesProfitMarginPage: React.FC = () => {
  const [data, setData] = useState<RptSalesProfitMargins>([]);
  const [loading, setLoading] = useState(false);
  const [fromCode, setFromCode] = useState<number | undefined>();
  const [toCode, setToCode] = useState<number | undefined>();
  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear().toString(),
  );
  const apiFetchRef = useRef(false);
  const [pageSize, setPageSize] = useState(50);
  const [pageNumber, setPageNumber] = useState(1);
  const [total, setTotal] = useState(0);
  const [reportType, setReportType] = useState<number>(0);

  const fetchData = async (download: boolean | null = false) => {
    if (apiFetchRef.current) return;
    apiFetchRef.current = true;
    setLoading(true);
    const startCode = fromCode ?? null;
    const endCode = toCode ?? null;

    if (download) {
      try {
        switch (reportType) {
          case 0:
            await safeApiCall(
              getSalesProfitMarginByMonth(
                Number(selectedYear ?? new Date().getFullYear()),
                startCode,
                endCode,
                null,
                null,
                true,
              ),
            );
            break;
          default:
            await safeApiCall(
              getSalesProfitMarginYr2Date(
                Number(selectedYear ?? new Date().getFullYear()),
                startCode,
                endCode,
                null,
                null,
                true,
              ),
            );
            break;
        }

        notifications.show({
          title: "File Download!",
          message: `Downloading CSV report`,
          color: MantineNotificationColor.Success,
          position: MantineNotificationPosition.TopCenter,
        });
      } catch (err) {
        notifications.show({
          title: "File Download!",
          message: `Unable to export CSV for this report. ${err}`,
          color: MantineNotificationColor.Error,
          position: MantineNotificationPosition.TopCenter,
        });
      }
    } else {
      let result;

      switch (reportType) {
        case 0:
          result = await safeApiCall(
            getSalesProfitMarginByMonth(
              Number(selectedYear ?? new Date().getFullYear()),
              startCode,
              endCode,
              pageSize,
              pageNumber,
            ),
          );
          break;
        default:
          result = await safeApiCall(
            getSalesProfitMarginYr2Date(
              Number(selectedYear ?? new Date().getFullYear()),
              startCode,
              endCode,
              pageSize,
              pageNumber,
            ),
          );
          break;
      }

      setData(result?.data ?? []);
      setTotal(result?.total ?? 0);
    }

    setLoading(false);
    apiFetchRef.current = false;
  };

  //Clear data on type switch
  useEffect(() => {
    setData([]); // reset when switching report type
    fetchData();
  }, [reportType]);

  useEffect(() => {
    fetchData();
  }, [pageNumber, pageSize, selectedYear]);

  const handleYearChange = (value: string | null, _option: ComboboxItem) => {
    if (value) setSelectedYear(value);
  };
  return (
    <Stack>
      <Group justify="space-between" align="end" gap="sm">
        <Title order={2}>Sales Profit Margin Report</Title>
        <Button
          onClick={() => {
            navigateTo(
              `/print/sales-profit-margins?rpt=${reportType}&y=${selectedYear}&f=${fromCode ?? ""}&t=${toCode ?? ""}`,
            );
          }}
          variant="gradient"
        >
          Print
        </Button>
      </Group>

      <Group align="end" gap="sm">
        <SalesMarginReportTypeSelection
          name="SalesProfiltMarginReportType"
          label="Choose Report Type"
          title="Defaults to monthly report"
          value={reportType}
          setValue={setReportType}
        />
        <YearSelect
          value={selectedYear}
          onChange={handleYearChange}
          title="Defaults to current year if blank"
        />

        <ProductCodeInput
          name="fromCode"
          label="From Product Code"
          value={fromCode}
          setValue={setFromCode}
          resetPage={() => setPageNumber(1)}
          maxLength={6} // e.g. max 6 digits
          min={0}
          max={999999}
        />
        <ProductCodeInput
          name="toCode"
          label="To Product Code"
          value={toCode}
          setValue={setToCode}
          resetPage={() => setPageNumber(1)}
          maxLength={6} // e.g. max 6 digits
          min={0}
          max={999999}
        />
        <Button
          size={"sm"}
          disabled={apiFetchRef.current}
          onClick={() => fetchData(false)}
        >
          Refresh
        </Button>
        <Button
          size={"sm"}
          disabled={apiFetchRef.current}
          onClick={() => {
            setFromCode(undefined);
            setToCode(undefined);
          }}
        >
          Clear Filters
        </Button>
        <Tooltip label="Download CSV">
          <ActionIcon size={"36"} onClick={() => fetchData(true)}>
            <IconDownload size={24} />
          </ActionIcon>
        </Tooltip>
      </Group>

      <Divider label="Leave above filters blank if you want to report on all data" />
      <Group justify="space-between" wrap="nowrap" mb="md">
        <Group justify="flex-end" wrap="nowrap" mb="md">
          <Text size="sm" c="dimmed">
            Showing
          </Text>
          <Select
            value={pageSize.toString()}
            onChange={(val) => setPageSize(Number(val))}
            data={["25", "50", "100", "200"].map((v) => ({
              value: v,
              label: v,
            }))}
          />
          <Text> items per page</Text>
        </Group>
        <Pagination
          total={Math.ceil(total / pageSize)}
          value={pageNumber}
          onChange={setPageNumber}
          siblings={1}
          boundaries={1}
        />
      </Group>
      <Table striped highlightOnHover withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Product</Table.Th>
            <Table.Th>Description</Table.Th>
            <Table.Th>Year</Table.Th>
            {reportType === 0 && <Table.Th>Month</Table.Th>}

            <Table.Th>Units Sold</Table.Th>
            <Table.Th>Avg Sell Price</Table.Th>
            <Table.Th>Current Cost Price</Table.Th>
            <Table.Th>Total Sales</Table.Th>
            <Table.Th>GP %</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {loading ? (
            <Table.Tr>
              <Table.Td colSpan={reportType === 0 ? 9 : 8}>
                <Center>
                  <Loader />
                </Center>
              </Table.Td>
            </Table.Tr>
          ) : (
            data.map((row, index) => (
              <Table.Tr key={`${index}-${row.id}`}>
                <Table.Td>{row.id}</Table.Td>
                <Table.Td>{row.description}</Table.Td>
                <Table.Td>{row.yearOrdered}</Table.Td>
                {reportType === 0 && <Table.Td>{row.month ?? ""}</Table.Td>}
                <Table.Td>{formatNZD(row.unitsSold)}</Table.Td>
                <Table.Td>{formatNZD(row.avgSellPrice)}</Table.Td>
                <Table.Td>{formatNZD(row.curCostPrice)}</Table.Td>
                <Table.Td>{formatNZD(row.totalSales)}</Table.Td>
                <Table.Td>
                  {formatNumberAsString(row.grossProfitMargin, 0)}
                </Table.Td>
              </Table.Tr>
            ))
          )}
        </Table.Tbody>
      </Table>
    </Stack>
  );
};
export default AdminSalesProfitMarginPage;
